import React, {useEffect, useRef, useCallback} from 'react'
import {VStack, HStack, Text, Image, useMultiStyleConfig, StackProps, TextProps, LinkProps, Link, Skeleton, IconButtonProps, FocusLock} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {Icon, DarkOverlay, IconButton} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

type ToastStatus = 'info' | 'warning' | 'success' | 'error' | 'loading' | undefined

interface ToastProps {
    title: string
    productImg?: string
    wishlistLabel: string
    isUserLogged?: boolean
    status: ToastStatus
    closeLabel: string
    children: string
    onClose: () => void
}

/**
 * @name Toast
 *
 * @component
 *
 * @description
 * Displays a message with optional product image and wishlist link.
 *
 * When `html.un-assistive` is active (UsableNet assistive view) or the toast
 * contains keyboard focus, the toast will not auto-dismiss — the user must
 * press the close button (WCAG 2.2 SC 2.2.1 Timing Adjustable).
 *
 * @param {ToastProps} props - The props for the Toast component.
 * @param {string} props.title - Title of the message to be displayed in the toast.
 * @param {string} [props.productImg] - Product image url.
 * @param {string} props.wishlistLabel - Label to be displayed in the wishlist button.
 * @param {boolean} [props.isUserLogged=false] - Indicates if the user is logged or not.
 * @param {ToastStatus} props.status - Status of the toast.
 * @param {string} props.children - Description to be displayed in the toast.
 * @param {function} props.onClose - On close function.
 *
 * @returns {React.FC<ToastProps>} - The Toast component.
 */
const Toast: React.FC<ToastProps> = ({title, productImg, wishlistLabel, isUserLogged = false, status = 'success', closeLabel, children, onClose}) => {
    const styles = useMultiStyleConfig('Toast') as Record<string, StackProps & TextProps & LinkProps & IconButtonProps>
    const {locale} = useMultiSite()

    const closeBtnRef = useRef<HTMLButtonElement | null>(null)
    const prevFocusedElement = useRef<HTMLElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const wishlistHref = isUserLogged ? `/${locale.alias}/account?tabID=wishlist` : `/${locale.alias}/wishlist`

    const isAssistiveMode = typeof document !== 'undefined' && document.documentElement.classList.contains('un-assistive')

    /**
     * Clears any pending auto-close timer.
     */
    const clearAutoCloseTimer = useCallback(() => {
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current)
            autoCloseTimerRef.current = null
        }
    }, [])

    /**
     * When focus enters the toast container, cancel auto-dismiss so the
     * user has time to interact with the content (WCAG 2.2 SC 2.2.1).
     */
    const handleFocus = useCallback(() => {
        clearAutoCloseTimer()
    }, [clearAutoCloseTimer])

    useEffect(() => {
        // Store the previously focused element
        prevFocusedElement.current = document.activeElement as HTMLElement
        // Focus the close button when Toast opens
        closeBtnRef.current?.focus()
        return () => {
            clearAutoCloseTimer()
            // Restore focus to the previously focused element on close
            prevFocusedElement.current?.focus()
        }
    }, [clearAutoCloseTimer])

    /**
     * When assistive mode is NOT active, auto-close the toast if focus
     * leaves the container (user tabbed away). This preserves the
     * existing ephemeral behaviour for non-assistive users while still
     * giving keyboard users enough time when they focus the toast.
     */
    const handleBlur = useCallback(
        (e: React.FocusEvent) => {
            if (isAssistiveMode) return
            // Only close when focus leaves the entire container
            if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
                autoCloseTimerRef.current = setTimeout(onClose, 2000)
            }
        },
        [isAssistiveMode, onClose],
    )

    return (
        <>
            <VStack {...styles.wrapper} ref={containerRef} onFocus={handleFocus} onBlur={handleBlur}>
                <FocusLock persistentFocus={false}>
                    <HStack {...styles.titleWrapper}>
                        <Text {...styles.title}>{title}</Text>
                        <IconButton
                            size="iconButtonMd"
                            ref={closeBtnRef}
                            aria-label={closeLabel}
                            onClick={onClose}
                            icon={<Icon name="IconClose" size="md" />}
                        />
                    </HStack>

                    <HStack {...styles.bodyWrapper}>
                        {productImg && <Image fallback={<Skeleton {...styles.productImageSkeleton} />} src={productImg} alt={title} {...styles.productImage} />}

                        <VStack {...styles.info}>
                            <Text {...styles.message} {...(status === 'error' ? styles.errorMessage : {})}>
                                {children}
                            </Text>

                            {isUserLogged ? (
                                <Link variant="alwaysUnderline" {...styles.wishlistLink} href={wishlistHref} onClick={onClose}>
                                    {wishlistLabel}
                                </Link>
                            ) : (
                                <Link
                                    variant="alwaysUnderline"
                                    as={RouteLink}
                                    {...styles.wishlistLink}
                                    to={{pathname: wishlistHref, state: {scrollToTop: true}}}
                                    onClick={onClose}
                                >
                                    {wishlistLabel}
                                </Link>
                            )}
                        </VStack>
                    </HStack>
                </FocusLock>
            </VStack>
            <DarkOverlay onClick={onClose} />
        </>
    )
}

export {Toast}
